/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Listbox } from '@headlessui/react';

// Sizer contributes intrinsic width based on longest label so panel can auto-size
function Sizer({ options }) {
  if (!options || options.length === 0) return null;
  const longest = options.reduce((a, b) => (String(b.label).length > String(a).length ? b.label : a), options[0].label);
  return (
    <div aria-hidden="true" className="invisible h-0 overflow-hidden whitespace-nowrap">
      <span className="pl-10 pr-4">{longest}</span>
    </div>
  );
}

// A simple chevron down icon component
const ChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-textSecondary">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
  </svg>
);

function OptionsPortal({ open, anchorRef, children, fitContent = true, maxWidthPx, minWidthPx, margin = 8, portalTarget }) {
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 0, maxWidth: 0 });
  const containerRef = useRef(null);

  // Determine the best container to render into (avoid inert on siblings of dialogs)
  const getDefaultContainer = () => {
    const anchor = anchorRef?.current;
    if (portalTarget && portalTarget instanceof HTMLElement) return portalTarget;
    if (!anchor) return document.body;

    // Prefer Headless UI's portal root or the nearest dialog/overlay container
    const hPortal = anchor.closest('[data-headlessui-portal]');
    if (hPortal) return hPortal;
    const dialog = anchor.closest('[role="dialog"], [aria-modal="true"]');
    if (dialog) return dialog;
    const overlay = anchor.closest('[data-overlay-container], [data-portal-root], [data-radix-portal]');
    if (overlay) return overlay;

    // NEW: detect the active top-level overlay root (Headless UI inert-others hides body children)
    try {
      const activeRoot = Array.from(document.body.children).find((el) => {
        const hidden = el.getAttribute?.('aria-hidden') === 'true' || el.hasAttribute?.('inert');
        return !hidden && el.contains(anchor);
      });
      if (activeRoot) return activeRoot;
    } catch (_) {}

    // Fallback: climb to the nearest ancestor that is NOT inert/aria-hidden and use its parent as container
    let el = anchor;
    let candidate = null;
    while (el && el !== document.body) {
      const isInert = (el.hasAttribute && el.hasAttribute('inert')) || el.getAttribute?.('aria-hidden') === 'true';
      if (isInert) break; // stop at boundary; last candidate is inside the active overlay
      candidate = el;
      el = el.parentElement;
    }
    // Use the parent of the deepest non-inert node (so we append alongside, not inside a button/input)
    if (candidate && candidate.parentElement) return candidate.parentElement;

  return document.body;
  };

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vw = document.documentElement.clientWidth || window.innerWidth;
      // Use viewport coordinates for position: fixed
      const desiredLeft = rect.left;
      const minWidth = typeof minWidthPx === 'number' ? Math.max(rect.width, minWidthPx) : rect.width;
      let left = desiredLeft;
      let maxWidth = (typeof maxWidthPx === 'number' ? maxWidthPx : vw) - left - margin;
      if (minWidth > maxWidth) {
        left = Math.max(margin, vw - minWidth - margin);
        maxWidth = vw - left - margin;
      }
      setPos({
        top: rect.bottom,
        left,
        minWidth,
        maxWidth,
      });
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef, maxWidthPx, margin]);

  if (!open) return null;
  const child = React.isValidElement(children)
    ? React.cloneElement(children, {
        style: {
          ...(children.props.style || {}),
          minWidth: pos.minWidth,
          maxWidth: pos.maxWidth,
          display: fitContent ? 'inline-block' : 'block',
        },
      })
    : children;

  // Pick a stable container once per open session to avoid remounting across renders
  if (!containerRef.current) {
    containerRef.current = getDefaultContainer();
  }
  const container = containerRef.current;

  return createPortal(
    <div style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 3000 }}>
      {child}
    </div>,
    container
  );
}

function CustomSelect({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className = '',
  buttonClassName = '',
  searchable = false,
  searchPlaceholder = 'Search…',
  virtualize = true,
  maxDropdownHeight = 320, // px
  itemHeight = 36, // px
  overscan = 6,
  dropdownFitContent = true,
  dropdownMaxWidth,
  optionPaddingLeft = 24, // px (was ~40px)
  centerOptions = false,
  renderInPortal = true, // set false when used inside a Menu
  portalContainer, // optional explicit portal container
  renderOption, // (option, { selected, active }) => ReactNode — custom dropdown item
  renderSelected, // (option) => ReactNode — custom trigger display
  dropdownMinWidth, // optional minimum width for the dropdown panel in px
}) {
  const selectedOption = options.find(option => option.value === value) || null;
  const btnRef = useRef(null);
  const optsRef = useRef(null);
  // Flag to detect an actual open event vs. re-renders from external state (e.g., price ticks)
  const justOpenedRef = useRef(false);

  const [query, setQuery] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(maxDropdownHeight);
  const lastScrollTopRef = useRef(0);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => String(o.label).toLowerCase().includes(q));
  }, [options, query]);

  const total = filteredOptions.length;
  const visibleCount = Math.max(1, Math.ceil(containerHeight / itemHeight) + (virtualize ? overscan : total));
  const startIndex = virtualize ? Math.max(0, Math.floor(scrollTop / itemHeight) - Math.floor(overscan / 2)) : 0;
  const endIndex = virtualize ? Math.min(total, startIndex + visibleCount) : total;
  const topOffset = virtualize ? startIndex * itemHeight : 0;
  const totalHeight = virtualize ? total * itemHeight : 'auto';

  useEffect(() => {
    // Reset scroll when query changes
    if (!optsRef.current) return;
    optsRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [query]);

  useEffect(() => {
    // Keep last known scroll; useful if options container re-mounts
    lastScrollTopRef.current = scrollTop;
  }, [scrollTop]);

  useEffect(() => {
    if (!optsRef.current) return;
    const ro = new ResizeObserver(() => {
      setContainerHeight(Math.min(maxDropdownHeight, optsRef.current.clientHeight || maxDropdownHeight));
    });
    ro.observe(optsRef.current);
    return () => ro.disconnect();
  }, [maxDropdownHeight]);

  const handleScroll = (e) => {
    const st = e.currentTarget.scrollTop;
    lastScrollTopRef.current = st;
    setScrollTop(st);
  };

  // Manually handle wheel to ensure scrolling works even if globals call preventDefault
  const handleWheel = (e) => {
    const el = optsRef.current;
    if (!el) return;
    const before = el.scrollTop;
    el.scrollTop += e.deltaY;
    if (el.scrollTop !== before) {
      // Sync refs immediately so any re-render before the async scroll event
      // doesn't revert the position via the scroll-restore IIFE.
      lastScrollTopRef.current = el.scrollTop;
      setScrollTop(el.scrollTop);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const renderOptionsPanel = (open, style) => (
    <Listbox.Options
      as="div"
      static
      style={style}
      className="rounded-md bg-backgroundSurface shadow-lg ring-1 ring-black ring-opacity-5 border border-borderDefault overflow-hidden focus:outline-none sm:text-sm py-1"
    >
      {/* Width sizer: contributes intrinsic width to allow panel to expand to longest label */}
      <Sizer options={filteredOptions} />
      {searchable && (
    <div className="p-2 border-b border-borderDefault bg-backgroundSurface">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
      className="w-full bg-backgroundInput border border-borderDefault rounded px-2 py-1 text-sm text-textPrimary placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      )}

      {total === 0 ? (
        <div className="p-3 text-sm text-textSecondary">No results</div>
      ) : (
        <div
          ref={optsRef}
          onScroll={handleScroll}
          onScrollCapture={(e) => e.stopPropagation()}
          onWheelCapture={handleWheel}
          onTouchMoveCapture={(e) => e.stopPropagation()}
          onPointerDownCapture={(e) => e.stopPropagation()}
          style={{ maxHeight: maxDropdownHeight, overflowY: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'auto', overscrollBehavior: 'contain' }}
        >
          {/* Restore scroll position when menu opens or re-renders while open */}
          {open && (() => {
            const el = optsRef.current;
            if (el) {
              if (justOpenedRef.current) {
                // First open: center the selected item
                const idx = filteredOptions.findIndex(o => o.value === value);
                if (idx >= 0) {
                  const target = idx * itemHeight - itemHeight * 2;
                  el.scrollTop = Math.max(0, target);
                  lastScrollTopRef.current = el.scrollTop;
                  setScrollTop(el.scrollTop);
                }
                justOpenedRef.current = false;
              } else {
                // Subsequent renders while open: restore last scrollTop
                el.scrollTop = lastScrollTopRef.current || el.scrollTop;
              }
            }
            return null;
          })()}
          <div style={{ height: totalHeight, position: 'relative' }}>
            <div style={virtualize ? { position: 'absolute', top: topOffset, left: 0, right: 0 } : undefined}>
              {filteredOptions.slice(startIndex, endIndex).map((option, index) => (
                <Listbox.Option
                  key={option.value ?? `${startIndex + index}`}
                  className={({ active, disabled }) => `relative cursor-default select-none pr-3 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${active && !disabled ? 'bg-secondary/30 text-textPrimary' : 'text-textPrimary'}`}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {({ selected, active }) => (
                    renderOption ? (
                      <span
                        className="flex items-center w-full relative"
                        style={{ height: itemHeight, paddingLeft: centerOptions ? undefined : optionPaddingLeft }}
                      >
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary">
                            <CheckIcon aria-hidden="true" />
                          </span>
                        )}
                        {renderOption(option, { selected, active })}
                      </span>
                    ) : (
                      <span
                        className={`block w-full whitespace-nowrap relative ${centerOptions ? 'text-center' : ''}`}
                        style={{ height: itemHeight, paddingLeft: centerOptions ? undefined : optionPaddingLeft }}
                        title={String(option.label)}
                      >
                        <span className={`${selected ? 'font-medium' : 'font-normal'}`}>{option.label}</span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary">
                            <CheckIcon aria-hidden="true" />
                          </span>
                        ) : null}
                      </span>
                    )
                  )}
                </Listbox.Option>
              ))}
            </div>
          </div>
        </div>
      )}
    </Listbox.Options>
  );

  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className={`relative min-w-0 ${className}`}>
          <Listbox.Button
            ref={btnRef}
            className={`relative w-full cursor-default rounded-md border border-borderDefault bg-backgroundInput py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm overflow-hidden ${buttonClassName}`}
            onClick={() => { justOpenedRef.current = !open; }}
            onKeyDown={(e) => {
              // If user triggers open via keyboard, mark as just-opened so we auto-scroll once
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                justOpenedRef.current = !open;
              }
            }}
          >
            <span className="block truncate text-textPrimary">
              {selectedOption
                ? (renderSelected ? renderSelected(selectedOption) : selectedOption.label)
                : placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDown aria-hidden="true" />
            </span>
          </Listbox.Button>

          {renderInPortal ? (
            <OptionsPortal open={open} anchorRef={btnRef} fitContent={dropdownFitContent} maxWidthPx={dropdownMaxWidth} minWidthPx={dropdownMinWidth} portalTarget={portalContainer}>
              {renderOptionsPanel(open)}
            </OptionsPortal>
          ) : (
            open && (
              <div className="absolute z-50 mt-1 w-full">
                {renderOptionsPanel(open)}
              </div>
            )
          )}
        </div>
      )}
    </Listbox>
  );
}

// Custom equality check to avoid re-renders on frequent parent updates (e.g., price ticks)
function optionsShallowEqual(a = [], b = []) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ai = a[i];
    const bi = b[i];
    if (ai?.value !== bi?.value || String(ai?.label) !== String(bi?.label)) return false;
  }
  return true;
}

const arePropsEqual = (prev, next) => {
  if (prev.value !== next.value) return false;
  if (!optionsShallowEqual(prev.options, next.options)) return false;
  if (prev.placeholder !== next.placeholder) return false;
  if (prev.className !== next.className) return false;
  if (prev.searchable !== next.searchable) return false;
  if (prev.virtualize !== next.virtualize) return false;
  if (prev.maxDropdownHeight !== next.maxDropdownHeight) return false;
  if (prev.itemHeight !== next.itemHeight) return false;
  if (prev.dropdownFitContent !== next.dropdownFitContent) return false;
  if (prev.dropdownMaxWidth !== next.dropdownMaxWidth) return false;
  if (prev.optionPaddingLeft !== next.optionPaddingLeft) return false;
  if (prev.centerOptions !== next.centerOptions) return false;
  if (prev.renderInPortal !== next.renderInPortal) return false;
  if (prev.renderOption !== next.renderOption) return false;
  if (prev.renderSelected !== next.renderSelected) return false;
  // ignore onChange and portalContainer identity changes
  return true;
};

export default React.memo(CustomSelect, arePropsEqual);
